import { useState, useCallback, useMemo, useRef } from 'react';
import { T, useGT } from 'gt-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CaretDownIcon, XIcon } from '@phosphor-icons/react';

type Operator = 'eq' | 'gte' | 'lte' | 'between';

const OPERATOR_LABELS: Record<Operator, string> = {
  eq: '= (equals)',
  gte: '>= (greater or equal)',
  lte: '<= (less or equal)',
  between: 'Between',
};

/** Parse operator:value entries from the URL filter string[] */
function parseFilterValues(values: string[]): {
  operator: Operator;
  from?: string;
  to?: string;
} {
  let hasGte: string | undefined;
  let hasLte: string | undefined;
  let hasEq: string | undefined;

  for (const entry of values) {
    const colonIdx = entry.indexOf(':');
    if (colonIdx === -1) continue;
    const op = entry.slice(0, colonIdx);
    const val = entry.slice(colonIdx + 1);
    if (op === 'gte') hasGte = val;
    else if (op === 'lte') hasLte = val;
    else if (op === 'eq') hasEq = val;
  }

  if (hasGte && hasLte) {
    return { operator: 'between', from: hasGte, to: hasLte };
  }
  if (hasGte) return { operator: 'gte', from: hasGte };
  if (hasLte) return { operator: 'lte', to: hasLte };
  if (hasEq) return { operator: 'eq', from: hasEq };
  return { operator: 'gte' as Operator };
}

/** Build a human-readable summary of the active filter */
function buildSummary(values: string[]): string | null {
  if (values.length === 0) return null;
  const parsed = parseFilterValues(values);
  const { operator, from, to } = parsed;

  if (operator === 'eq' && from) return `= ${from}`;
  if (operator === 'gte' && from) return `>= ${from}`;
  if (operator === 'lte' && to) return `<= ${to}`;
  if (operator === 'between' && (from || to)) {
    return `${from ?? '?'} – ${to ?? '?'}`;
  }
  return null;
}

interface NumericFilterProps {
  label: string;
  value: string[];
  onChange: (values: string[] | undefined) => void;
  defaultOperator?: Operator;
}

export function NumericFilter({
  label,
  value,
  onChange,
  defaultOperator = 'gte',
}: NumericFilterProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const parsed = useMemo(() => parseFilterValues(value), [value]);
  const [operator, setOperator] = useState<Operator>(
    value.length > 0 ? parsed.operator : defaultOperator,
  );
  const summary = useMemo(() => buildSummary(value), [value]);
  const isActive = value.length > 0 && summary !== null;

  // Local input state for debounced updates
  const [fromInput, setFromInput] = useState(parsed.from ?? '');
  const [toInput, setToInput] = useState(parsed.to ?? '');
  const fromTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const toTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Sync local inputs when URL state changes externally
  const lastValueRef = useRef(value);
  if (value !== lastValueRef.current) {
    lastValueRef.current = value;
    const p = parseFilterValues(value);
    if (value.length > 0) {
      setOperator(p.operator);
    }
    if (p.from !== undefined && p.from !== fromInput) setFromInput(p.from);
    if (p.to !== undefined && p.to !== toInput) setToInput(p.to);
    if (value.length === 0) {
      clearTimeout(fromTimer.current);
      clearTimeout(toTimer.current);
      setOperator(defaultOperator);
      setFromInput('');
      setToInput('');
    }
  }

  const buildValues = useCallback(
    (op: Operator, from?: string, to?: string): string[] | undefined => {
      const result: string[] = [];
      const isNum = (v?: string) => v != null && v !== '' && !isNaN(Number(v));

      if (op === 'eq' && isNum(from)) {
        result.push(`eq:${from}`);
      } else if (op === 'gte' && isNum(from)) {
        result.push(`gte:${from}`);
      } else if (op === 'lte' && isNum(to)) {
        result.push(`lte:${to}`);
      } else if (op === 'between') {
        // Between requires BOTH ends. Emit nothing until both are valid
        // numbers — a half-filled range would otherwise filter too early.
        if (isNum(from) && isNum(to)) {
          // Normalize: if min > max, swap so the emitted range is valid.
          const fromNum = Number(from);
          const toNum = Number(to);
          const lo = fromNum <= toNum ? fromNum : toNum;
          const hi = fromNum <= toNum ? toNum : fromNum;
          result.push(`gte:${lo}`);
          result.push(`lte:${hi}`);
        }
      }
      return result.length > 0 ? result : undefined;
    },
    [],
  );

  const DEBOUNCE_MS = 400;

  const handleOperatorChange = useCallback(
    (newOp: Operator) => {
      setOperator(newOp);

      let newValues: string[] | undefined;
      if (newOp === 'eq' || newOp === 'gte') {
        newValues = buildValues(newOp, fromInput || toInput);
      } else if (newOp === 'lte') {
        newValues = buildValues(newOp, undefined, toInput || fromInput);
      } else if (newOp === 'between') {
        newValues = buildValues(newOp, fromInput, toInput);
      }

      if (newValues !== undefined) {
        onChange(newValues);
      }
    },
    [fromInput, toInput, onChange, buildValues],
  );

  const handleFromInputChange = useCallback(
    (raw: string) => {
      setFromInput(raw);
      clearTimeout(fromTimer.current);
      fromTimer.current = setTimeout(() => {
        if (operator === 'eq' || operator === 'gte') {
          onChange(buildValues(operator, raw));
        } else if (operator === 'between') {
          onChange(buildValues(operator, raw, toInput));
        }
      }, DEBOUNCE_MS);
    },
    [operator, toInput, onChange, buildValues],
  );

  const handleToInputChange = useCallback(
    (raw: string) => {
      setToInput(raw);
      clearTimeout(toTimer.current);
      toTimer.current = setTimeout(() => {
        if (operator === 'lte') {
          onChange(buildValues(operator, undefined, raw));
        } else if (operator === 'between') {
          onChange(buildValues(operator, fromInput, raw));
        }
      }, DEBOUNCE_MS);
    },
    [operator, fromInput, onChange, buildValues],
  );

  const handleClear = useCallback(() => {
    clearTimeout(fromTimer.current);
    clearTimeout(toTimer.current);
    onChange(undefined);
    setOperator(defaultOperator);
    setFromInput('');
    setToInput('');
    setOpen(false);
  }, [onChange, defaultOperator]);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-7 justify-between font-normal max-w-[200px] ${isActive ? 'border-foreground' : 'text-muted-foreground'}`}
          >
            <span className="truncate">
              {isActive ? `${label}: ${summary}` : label}
            </span>
            <CaretDownIcon className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-3" align="start">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{label}</p>
              {isActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={handleClear}
                >
                  <XIcon className="h-3 w-3 mr-1" />
                  <T context="button to clear the numeric filter">Clear</T>
                </Button>
              )}
            </div>

            <Select
              value={operator}
              onValueChange={(v) => handleOperatorChange(v as Operator)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(OPERATOR_LABELS) as [Operator, string][]).map(
                  ([op, lbl]) => (
                    <SelectItem key={op} value={op} className="text-xs">
                      {lbl}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>

            {(operator === 'eq' ||
              operator === 'gte' ||
              operator === 'between') && (
              <div className="space-y-1">
                {operator === 'between' && (
                  <p className="text-xs text-muted-foreground">
                    <T context="label for the minimum value input in a between numeric filter">
                      Min
                    </T>
                  </p>
                )}
                <Input
                  type="number"
                  value={fromInput}
                  onChange={(e) => handleFromInputChange(e.target.value)}
                  placeholder={gt('Enter value', {
                    context: 'placeholder for numeric filter value input',
                  })}
                  className="h-8 text-sm"
                />
              </div>
            )}

            {(operator === 'lte' || operator === 'between') && (
              <div className="space-y-1">
                {operator === 'between' && (
                  <p className="text-xs text-muted-foreground">
                    <T context="label for the maximum value input in a between numeric filter">
                      Max
                    </T>
                  </p>
                )}
                <Input
                  type="number"
                  value={toInput}
                  onChange={(e) => handleToInputChange(e.target.value)}
                  placeholder={gt('Enter value', {
                    context: 'placeholder for numeric filter value input',
                  })}
                  className="h-8 text-sm"
                />
              </div>
            )}

            {operator === 'between' && !isActive && (fromInput || toInput) && (
              <p className="text-xs text-muted-foreground">
                <T context="helper text when between filter has only one value filled in">
                  Enter both Min and Max to apply the filter.
                </T>
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
